import { apiFetch } from './http';

import { supabase } from '../lib/supabaseClient';

export type YearlyInventoryRequestValidationApiLine = {
  lineId: string | null;
  label: string;
  panelKey: string;
  panelName: string;
  issues: string[];
  instructions: string[];
};

export class YearlyInventoryRequestValidationError extends Error {
  invalidLines: YearlyInventoryRequestValidationApiLine[];

  constructor(message: string, invalidLines: YearlyInventoryRequestValidationApiLine[]) {
    super(message);
    this.name = 'YearlyInventoryRequestValidationError';
    this.invalidLines = invalidLines;
  }
}

async function handleJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    await throwApiError(res);
  }
  return res.json() as Promise<T>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeInvalidLines(value: unknown): YearlyInventoryRequestValidationApiLine[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry): YearlyInventoryRequestValidationApiLine | null => {
    if (!isRecord(entry)) return null;
    const issues = Array.isArray(entry.issues)
      ? entry.issues.map((issue) => String(issue || '').trim()).filter(Boolean)
      : [];
    const instructions = Array.isArray(entry.instructions)
      ? entry.instructions.map((instruction) => String(instruction || '').trim()).filter(Boolean)
      : [];
    return {
      lineId: typeof entry.lineId === 'string' ? entry.lineId : null,
      label: String(entry.label || 'Request row'),
      panelKey: typeof entry.panelKey === 'string' && entry.panelKey.trim() ? entry.panelKey.trim() : 'needs',
      panelName: typeof entry.panelName === 'string' && entry.panelName.trim() ? entry.panelName.trim() : 'Needs',
      issues,
      instructions,
    };
  }).filter((entry): entry is YearlyInventoryRequestValidationApiLine => entry != null && entry.issues.length > 0);
}

async function throwApiError(res: Response): Promise<never> {
  const text = await res.text();
  if (!text) throw new Error(`Request failed with status ${res.status}`);
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new Error(text);
  }
  if (isRecord(parsed)) {
    const message = typeof parsed.message === 'string' && parsed.message.trim()
      ? parsed.message.trim()
      : (typeof parsed.error === 'string' && parsed.error.trim() ? parsed.error.trim() : text);
    if (parsed.error === 'request_validation_failed') {
      const invalidLines = normalizeInvalidLines(parsed.invalidLines);
      if (invalidLines.length) {
        throw new YearlyInventoryRequestValidationError(message, invalidLines);
      }
    }
    throw new Error(message);
  }
  throw new Error(text);
}

export type YearlyInventoryCampaign = {
  campaign_id: string;
  campaign_year: number;
  title: string;
  kickoff_message: string | null;
  starts_at: string | null;
  ends_at: string | null;
  status: string;
  kickoff_broadcast_id: string | null;
  settings: Record<string, unknown>;
  master_approval_publication?: {
    publishedAt: string;
    publishedByProfileId: string | null;
    version: number | null;
  } | null;
  created_by: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  included_location_count?: number;
  /** True when this campaign's Planned Purchases page has been shared with the caller. */
  planned_purchases_shared_with_me?: boolean;
  /** True when this campaign's Planned Transfers page has been shared with the caller. */
  planned_transfers_shared_with_me?: boolean;
};

export type UpdateYearlyInventoryCampaignPayload = {
  title?: string;
  kickoffMessage?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
};

export type YearlyInventoryReasonType =
  | 'replacement'
  | 'addition'
  | 'not_used'
  | 'appearance'
  | 'needs_service'
  | 'other';

export type YearlyInventoryReplacementReasonOption = {
  key: string;
  label: string;
  active: boolean;
  sort_order: number;
};

export type YearlyInventoryRequestLineTransferLink = {
  line_transfer_link_id: string;
  request_line_id: string;
  request_id: string;
  transfer_id: string;
  transfer_line_id: string;
  from_location_id: string;
  to_location_id: string;
  item_id: string | null;
  external_item_name?: string | null;
  external_item_code?: string | null;
  external_item_type?: 'external' | 'accessory' | string | null;
  qty: number;
  created_at: string;
  transfer?: {
    transfer_id: string;
    doc_no: string | null;
    status: string;
    from_location_id: string;
    to_location_id: string;
  } | null;
};

export type YearlyInventoryRequestLine = {
  request_line_id: string;
  request_id: string;
  item_id: string | null;
  product_id: string | null;
  item?: {
    item_id: string;
    item_code: string | null;
    name_internal: string | null;
    brand?: string | null;
    manufacturer_name?: string | null;
    model_name?: string | null;
    model_number?: string | null;
    replacement_cost?: number | null;
    purchase_cost_avg?: number | null;
    purchase_cost_last?: number | null;
  } | null;
  replaced_item_id?: string | null;
  replaced_item?: {
    item_id: string;
    item_code: string | null;
    name_internal: string | null;
    brand?: string | null;
    manufacturer_name?: string | null;
    model_name?: string | null;
    model_number?: string | null;
    replacement_cost?: number | null;
    purchase_cost_avg?: number | null;
    purchase_cost_last?: number | null;
  } | null;
  product?: {
    product_id: string;
    product_code: string | null;
    name_public: string | null;
  } | null;
  custom_item_name: string | null;
  custom_make?: string | null;
  custom_model?: string | null;
  external_url: string | null;
  requested_unit_price?: number | null;
  resolved_unit_cost?: number | null;
  qty_needed: number;
  reason_type: YearlyInventoryReasonType;
  reason_detail: string | null;
  replacement_reason_key?: string | null;
  is_external_item: boolean;
  priority_rank?: number;
  preferred_procurement_type?: 'stock' | 'purchase' | 'cross_rental' | 'transfer' | null;
  preferred_vendor_id?: string | null;
  preferred_supplier_id?: string | null;
  purchase_po_number?: string | null;
  purchase_status?: 'planned' | 'partial' | 'purchased' | string | null;
  purchase_execution_metadata?: Record<string, unknown> | null;
  purchase_marked_purchased_at?: string | null;
  purchase_marked_purchased_by?: string | null;
  target_margin_pct?: number | null;
  expected_unit_cost?: number | null;
  convert_to_permanent_on_receive?: boolean;
  internal_transfer_plan?: Record<string, unknown> | null;
  approved_transfer_metadata?: Record<string, unknown> | null;
  approved_transfer_qty?: number | null;
  transfer_qty?: number | null;
  purchase_qty?: number | null;
  requested_subtotal?: number | null;
  transfer_subtotal?: number | null;
  purchase_subtotal?: number | null;
  transfer_links?: YearlyInventoryRequestLineTransferLink[];
  review_decision?: 'approved' | 'rejected' | string | null;
  review_note?: string | null;
  reviewed_at?: string | null;
  reviewer_profile_id?: string | null;
  admin_review_decision?: 'approved' | 'rejected' | string | null;
  admin_review_note?: string | null;
  admin_reviewed_at?: string | null;
  admin_reviewer_profile_id?: string | null;
  panel_key?: string | null;
  line_order: number;
  merged_source_line_id?: string | null;
  merged_into_request_line_id?: string | null;
  requested_by: string | null;
  requested_by_profile?: {
    id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  screenshot_count?: number;
  screenshot_file_ids?: string[];
  created_at: string;
  updated_at: string;
};

export type YearlyInventoryPlannerLine = {
  planner_line_id: string;
  board_id: string;
  panel_key?: string | null;
  line_order: number;
  item_id: string | null;
  product_id: string | null;
  item?: YearlyInventoryRequestLine['item'];
  replaced_item_id?: string | null;
  replaced_item?: YearlyInventoryRequestLine['replaced_item'];
  product?: YearlyInventoryRequestLine['product'];
  custom_item_name: string | null;
  custom_make?: string | null;
  custom_model?: string | null;
  external_url: string | null;
  requested_unit_price?: number | null;
  resolved_unit_cost?: number | null;
  qty_needed: number;
  reason_type: YearlyInventoryReasonType;
  reason_detail: string | null;
  replacement_reason_key?: string | null;
  is_external_item: boolean;
  priority_rank?: number;
  requested_by: string | null;
  requested_by_profile?: YearlyInventoryRequestLine['requested_by_profile'];
  screenshot_count?: number;
  screenshot_file_ids?: string[];
  created_at: string;
  updated_at: string;
};

export type YearlyInventoryAttachmentTarget = {
  kind: 'planner' | 'request';
  lineId: string;
};

export type YearlyInventoryAttachmentLinkSource = YearlyInventoryAttachmentTarget;

export type YearlyInventoryLineAttachment = {
  attachment_link_id: string;
  attachment_file_id: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  image_width?: number | null;
  image_height?: number | null;
  created_at: string;
  signed_url: string | null;
  preview_url?: string | null;
};

export type YearlyInventoryAttachmentUploadStatus = 'uploading' | 'completing' | 'uploaded' | 'failed';

export type YearlyInventoryAttachmentUploadSession = {
  attachment_link_id: string;
  attachment_file_id: string;
  client_index: number;
  storage_bucket: string;
  storage_path: string;
  upload_token: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  upload_expires_at: string;
};

export type YearlyInventoryPlannerBoard = {
  board_id: string;
  owner_profile_id: string;
  notes: string | null;
  updated_at: string;
  panels: Array<{ key: string; name: string }>;
  lines: YearlyInventoryPlannerLine[];
};

export type YearlyInventoryRequestFulfillmentTransfer = {
  fulfillment_transfer_id: string;
  request_id?: string;
  transfer_id: string;
  linked_by: string | null;
  linked_at: string;
  note: string | null;
  transfer?: {
    transfer_id: string;
    doc_no: string | null;
    status: string;
    from_location_id: string;
    to_location_id: string;
  } | null;
};

export type YearlyInventoryRequest = {
  request_id: string;
  campaign_id: string;
  location_id: string;
  region_id: string | null;
  status: string;
  draft_owner_profile_id: string | null;
  submitted_by: string | null;
  submitted_by_profile?: {
    id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  submitted_at: string | null;
  manager_reviewer_profile_id: string | null;
  manager_reviewer_profile?: {
    id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  manager_reviewed_at: string | null;
  manager_notes: string | null;
  regional_rollup_id: string | null;
  admin_reviewer_profile_id: string | null;
  admin_reviewer_profile?: {
    id: string;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  admin_reviewed_at: string | null;
  admin_notes: string | null;
  metadata: Record<string, unknown>;
  fulfillment_transfers?: YearlyInventoryRequestFulfillmentTransfer[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  location?: {
    location_id: string;
    name: string;
    display_name?: string | null;
    region_id?: string | null;
    yearly_gross_revenue?: number | null;
    gross_revenue_year?: number | null;
    avg_yearly_gross_revenue?: number | null;
    avg_yearly_gross_revenue_sample_size?: number;
  } | null;
  campaign?: {
    campaign_id: string;
    campaign_year?: number | null;
    title?: string | null;
    status?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    settings?: Record<string, unknown>;
  } | null;
  last_activity?: {
    created_at: string;
    event_type?: string | null;
    actor_profile_id?: string | null;
    actor?: {
      id: string;
      full_name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    } | null;
  } | null;
  lines?: YearlyInventoryRequestLine[];
  line_count?: number;
  subtotal_total?: number;
  subtotal_by_panel?: Record<string, number>;
  active_assigned_profile_ids?: string[];
  assigned_profile_names?: string[];
  assigned_display?: string;
  location_access_profiles?: Array<{ id: string; label: string }>;
  can_manage?: boolean;
  can_edit?: boolean;
  can_unsubmit?: boolean;
  region_name?: string;
  regional_manager_assigned?: boolean;
  regional_manager_names?: string[];
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by?: string | null;
  deleted_by_name?: string | null;
  deleted_previous_status?: string | null;
};

export type AssignedYearlyInventoryRequest = Pick<
  YearlyInventoryRequest,
  'request_id' | 'campaign_id' | 'location_id' | 'status' | 'created_at' | 'updated_at' | 'location' | 'campaign' | 'can_edit'
>;

export type YearlyInventoryMasterSourceLine = YearlyInventoryRequestLine & {
  source_request_id: string;
  source_request_status: string;
  source_location_id: string;
  source_location_name: string;
  source_region_id: string | null;
  can_edit: boolean;
};

export type YearlyInventoryMasterGroup = {
  group_id: string;
  group_type: 'item' | 'external';
  item_id: string | null;
  product_id: string | null;
  item_name: string;
  sku: string | null;
  qty_total: number;
  requested_qty_total?: number;
  transfer_qty_total?: number;
  purchase_qty_total?: number;
  requested_subtotal_total?: number;
  transfer_subtotal_total?: number;
  subtotal_total: number;
  source_lines: YearlyInventoryMasterSourceLine[];
};

export type YearlyInventoryPurchaseSummary = {
  requestedQty: number;
  requestedCost: number;
  transferQty: number;
  transferCost: number;
  purchaseQty: number;
  purchaseCost: number;
  approvedQty: number;
  approvedCost: number;
  waitingQty: number;
  waitingCost: number;
  rejectedQty: number;
  rejectedCost: number;
};

export type YearlyInventoryMasterRequest = {
  virtual_id: string;
  campaign: YearlyInventoryCampaign;
  request_count: number;
  source_line_count: number;
  purchase_summary?: YearlyInventoryPurchaseSummary;
  groups: YearlyInventoryMasterGroup[];
  requests: Array<{
    request_id: string;
    location_id: string;
    location_name: string;
    status: string;
    line_count: number;
    can_edit: boolean;
  }>;
};

export type YearlyInventoryMasterPlannedTransferStatus =
  | 'ready'
  | 'in_progress'
  | 'transferred'
  | 'on_hold';

export type YearlyInventoryMasterPlannedTransferLine = {
  requestLineId: string;
  requestId: string;
  itemId: string | null;
  reasonType?: YearlyInventoryReasonType | string | null;
  externalItemName?: string | null;
  externalItemCode?: string | null;
  externalItemType?: 'external' | 'accessory' | string | null;
  itemCode: string | null;
  itemName: string | null;
  itemLabel: string;
  sourceLocationId: string;
  sourceLocationName: string;
  requestingLocationId: string;
  requestingLocationName: string;
  requestStatus: string | null;
  plannedQty: number;
  transferredQty: number;
  /** @deprecated Use transferredQty. */
  createdQty: number;
  remainingQty: number;
  unitCost: number;
  transferStatus: YearlyInventoryMasterPlannedTransferStatus;
  transferOrderReference: string | null;
  sourceCapacity?: {
    status: 'available' | 'short' | 'inactive';
    requiredQty: number;
    ownedQty: number;
    sourceableQty: number;
    shortageQty: number;
    message: string | null;
  };
  existingLinks: NonNullable<YearlyInventoryRequestLine['transfer_links']>;
};

export type YearlyInventoryMasterPlannedTransferBatch = {
  batchId: string;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  plannedQty: number;
  transferredQty: number;
  /** @deprecated Use transferredQty. */
  createdQty: number;
  remainingQty: number;
  lineCount: number;
  itemCount: number;
  status: Exclude<YearlyInventoryMasterPlannedTransferStatus, 'on_hold'>;
  transferOrderIds: string[];
  transferOrders: Array<{
    transfer_id: string;
    doc_no: string | null;
    status: string | null;
  }>;
  lines: YearlyInventoryMasterPlannedTransferLine[];
};

export type YearlyInventoryMasterPlannedTransferSummary = {
  plannedTransferCount: number;
  readyTransferCount: number;
  transferredTransferCount: number;
  inProgressTransferCount: number;
  /** @deprecated Use transferredTransferCount. */
  createdTransferCount: number;
  /** @deprecated Use inProgressTransferCount. */
  partialTransferCount: number;
  plannedQty: number;
  transferredQty: number;
  /** @deprecated Use transferredQty. */
  createdQty: number;
  remainingQty: number;
  transferReferenceCount: number;
  invalidSourceLineCount?: number;
  sourceCapacityIssueCount?: number;
  transferOrderIds: string[];
};

export type YearlyInventoryPlannedTransferReturnRetireRow = {
  requestLineId: string;
  requestId: string;
  itemId: string | null;
  itemCode: string | null;
  itemName: string;
  itemLabel: string;
  make: string | null;
  model: string | null;
  qty: number;
  priority: number;
  action: string;
  note: string | null;
  sourceLocationId: string | null;
  sourceLocationName: string;
  addedBy: string;
  addedAt: string | null;
};

export type YearlyInventoryMasterPlannedTransfersResponse = {
  ok?: boolean;
  access?: YearlyInventoryPlannedPurchaseAccess;
  campaign?: YearlyInventoryCampaign;
  summary: YearlyInventoryMasterPlannedTransferSummary;
  transfers: YearlyInventoryMasterPlannedTransferBatch[];
  returnRetire?: {
    rows: YearlyInventoryPlannedTransferReturnRetireRow[];
    sourceLineCount: number;
    qtyTotal: number;
  };
};

export type YearlyInventoryMasterPlannedPurchaseDestination = {
  requestLineId: string;
  requestId: string;
  itemId: string | null;
  productId?: string | null;
  itemCode: string | null;
  itemName: string | null;
  itemLabel: string;
  itemMake?: string | null;
  itemModel?: string | null;
  externalUrl?: string | null;
  isExternalItem?: boolean;
  customItemName?: string | null;
  reasonType?: YearlyInventoryReasonType | string | null;
  reasonDetail?: string | null;
  purchaseNote?: string | null;
  priorityRank?: number | null;
  panelKey?: string | null;
  requestingLocationId: string;
  requestingLocationName: string;
  requestStatus: string | null;
  approvalStatus: 'approved' | 'waiting' | 'rejected' | 'excluded' | string;
  procurementType: 'purchase' | 'transfer' | string | null;
  purchaseQty: number;
  unitCost: number;
  purchaseCost: number;
  purchasedQty?: number;
  purchasedCost?: number;
  openPurchaseQty?: number;
  openPurchaseCost?: number;
  purchaseAllocations?: Array<{
    locationId: string;
    locationName: string;
    qty: number;
  }>;
  subtotal?: number;
  purchaseVendorId?: string | null;
  purchaseSupplierId?: string | null;
  purchasePoNumber?: string | null;
  purchaseStatus?: 'planned' | 'partial' | 'purchased' | string;
  purchaseMarkedPurchasedAt?: string | null;
  purchaseMarkedPurchasedBy?: string | null;
  createdDraftTransferQty: number;
  remainingDraftTransferQty: number;
  canCreateDraftTransfer: boolean;
  existingLinks: NonNullable<YearlyInventoryRequestLine['transfer_links']>;
};

export type YearlyInventoryMasterPlannedPurchaseItem = {
  itemGroupId: string;
  itemId: string | null;
  productId: string | null;
  itemCode: string | null;
  itemName: string;
  itemLabel: string;
  purchaseQty: number;
  purchaseCost: number;
  approvedQty: number;
  approvedCost: number;
  waitingQty: number;
  waitingCost: number;
  rejectedQty: number;
  rejectedCost: number;
  purchasedQty: number;
  purchasedCost: number;
  openPurchaseQty: number;
  openPurchaseCost: number;
  destinationCount: number;
  destinations: YearlyInventoryMasterPlannedPurchaseDestination[];
};

export type YearlyInventoryMasterPlannedPurchaseRoute = {
  batchId: string;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
  plannedQty: number;
  createdQty: number;
  remainingQty: number;
  lineCount: number;
  itemCount: number;
  status: 'ready' | 'partial' | 'created';
  transferOrderIds: string[];
  transferOrders: Array<{
    transfer_id: string;
    doc_no: string | null;
    status: string | null;
  }>;
  lines: Array<YearlyInventoryMasterPlannedPurchaseDestination & {
    sourceLocationId: string;
    sourceLocationName: string;
    plannedQty: number;
    createdQty: number;
    remainingQty: number;
  }>;
};

export type YearlyInventoryMasterPlannedPurchaseSummary = {
  purchaseLineCount: number;
  purchaseItemCount: number;
  destinationCount: number;
  purchaseQty: number;
  purchaseCost: number;
  approvedQty: number;
  approvedCost: number;
  waitingQty: number;
  waitingCost: number;
  rejectedQty: number;
  rejectedCost: number;
  draftTransferQty: number;
  createdDraftTransferQty: number;
  remainingDraftTransferQty: number;
  draftTransferRouteCount: number;
  createdDraftTransferRouteCount: number;
  readyDraftTransferRouteCount: number;
  purchasedQty: number;
  purchasedCost: number;
  openPurchaseQty: number;
  openPurchaseCost: number;
  transferOrderIds: string[];
};

export type YearlyInventoryPlannedPurchaseAccess = {
  /** Row edits are allowed. Publishing and 998 draft transfers stay admin-only. */
  canEdit: boolean;
  isAdmin: boolean;
  /** Access comes from a share rather than the admin role. */
  sharedWithMe: boolean;
};

export type YearlyInventoryPlannedPurchaseShare = {
  share_id: string;
  profile_id: string;
  name: string;
  email: string | null;
  shared_at: string;
};

export type YearlyInventoryPlannedPurchaseShareCandidate = {
  profile_id: string;
  name: string;
  email: string | null;
};

export type YearlyInventoryPlannedPurchaseSharesResponse = {
  ok?: boolean;
  campaignClosed: boolean;
  shares: YearlyInventoryPlannedPurchaseShare[];
  candidates: YearlyInventoryPlannedPurchaseShareCandidate[];
};

export type YearlyInventoryPlannedTransferSharesResponse = YearlyInventoryPlannedPurchaseSharesResponse;

export type YearlyInventoryMasterPlannedPurchasesResponse = {
  ok?: boolean;
  access?: YearlyInventoryPlannedPurchaseAccess;
  campaign?: YearlyInventoryCampaign;
  receivingLocation: {
    location_id: string | null;
    siteid: string | null;
    name: string | null;
    display_name: string | null;
  } | null;
  summary: YearlyInventoryMasterPlannedPurchaseSummary;
  items: YearlyInventoryMasterPlannedPurchaseItem[];
  routes: YearlyInventoryMasterPlannedPurchaseRoute[];
  lineOrder?: string[];
  requests?: Array<{
    request_id: string;
    location_id: string | null;
    location_name: string | null;
    status: string | null;
  }>;
  createdTransferIds?: string[];
  createdLinkCount?: number;
};

export type YearlyInventoryPurchaseVendorOption = {
  vendor_id: string;
  name: string;
  active: boolean;
};

export type YearlyInventoryRecommendation = {
  campaign_id: string;
  regional_rollup_id: string;
  request_line_id: string;
  destination_location_id: string;
  candidate_source_location_id: string;
  item_id: string;
  requested_qty: number;
  suggested_transfer_qty: number;
  source_on_hand_qty: number;
  source_committed_qty_30d: number;
  source_available_transferable_qty: number;
  projected_source_coverage_days_after_transfer: number;
  destination_coverage_days_before_transfer: number;
  destination_coverage_days_after_transfer: number;
  dest_demand_score: number;
  source_usage_inverse_score: number;
  source_availability_score: number;
  revenue_opportunity_score: number;
  network_fit_score: number;
  total_recommendation_score: number;
  can_fulfill_via_transfer: boolean;
  is_primary_recommendation: boolean;
  is_permanent_transfer_candidate: boolean;
  confidence_level: 'low' | 'medium' | 'high';
  confidence_score: number;
  recommendation_rank: number;
  recommendation_reasons: Record<string, unknown>;
  policy_thresholds_used: Record<string, unknown>;
  scoring_version: string;
  computed_at: string;
};

export type YearlyItemActivityMetric = {
  qtyTransferredIn: number;
  timesTransferredIn: number;
  timesRented: number;
  qtyRented?: number;
};

export type YearlyItemLocationActivityRow = YearlyItemActivityMetric & {
  locationId: string;
  locationName: string;
  siteid?: string | null;
  r2Siteiid?: number | null;
};

export type YearlyItemLocationActivity = {
  totals: YearlyItemActivityMetric;
  locations: YearlyItemLocationActivityRow[];
};

export type YearlyItemLocationFinancialRow = {
  locationId: string;
  locationName: string;
  siteid?: string | null;
  r2Siteiid?: number | null;
  netRevenue: number;
  grossRevenue: number;
  purchaseCostBasis: number;
  unitPurchaseCost: number | null;
  inventoryQty: number;
  assetCount: number;
  ownedExposureDays?: number | null;
  ownedExposureRatio?: number | null;
  annualizedNetRevenue?: number | null;
  projectedTwoYearRoi?: number | null;
  revenueToCostRatio: number | null;
  roiPerOwnedUnit?: number | null;
  historicalPeakQty?: number;
  averageConcurrentQty?: number;
  peakUtilizationRatio?: number | null;
  averageUtilizationRatio?: number | null;
  revenueLineCount: number;
  invoiceAdjustedLineCount: number;
  kitComponentLineCount: number;
  purchaseCostSource?: string | null;
};

export type YearlyItemLocationFinancials = {
  totals: Omit<YearlyItemLocationFinancialRow, 'locationId' | 'locationName' | 'siteid' | 'r2Siteiid'>;
  locations: YearlyItemLocationFinancialRow[];
};

export type YearlyAssetLocationFinancialRow = {
  locationId: string;
  locationName: string;
  siteid?: string | null;
  r2Siteiid?: number | null;
  netRevenue: number;
  grossRevenue: number;
  usageCount: number;
};

export type YearlyAssetLocationFinancials = {
  assetId: string;
  barcode?: string | null;
  serialNumber?: string | null;
  status?: string | null;
  itemId?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  purchaseCost: number;
  homeLocationId?: string | null;
  homeLocationName?: string | null;
  currentLocationId?: string | null;
  currentLocationName?: string | null;
  totals: {
    netRevenue: number;
    grossRevenue: number;
    usageCount: number;
    purchaseCost: number;
    revenueToCostRatio: number | null;
  };
  locations: YearlyAssetLocationFinancialRow[];
};

export type YearlyInventoryTransferAvailabilityParams = {
  windowStart?: string;
  windowEnd?: string;
  bufferedWindowStart?: string;
  bufferedWindowEnd?: string;
  bufferDays?: number;
  lookbackMonths?: number;
  lookbackStart?: string;
  lookbackEnd?: string;
  utilizationGuardPct?: number;
  safetyStock?: number;
  utilizationMode?: 'average' | 'peak';
  financialYear?: number;
};

export type YearlyInventoryTransferAvailabilityGroupInput = {
  groupId: string;
  itemId?: string | null;
  requestedQty: number;
  targetLocationIds?: string[];
  targetLines?: Array<{
    requestLineId?: string | null;
    locationId: string;
    qty: number;
    reasonType?: YearlyInventoryReasonType | string | null;
  }>;
};

export type YearlyInventoryTransferAvailabilityAssetEarningLocation = {
  locationId: string | null;
  locationName: string;
  netRevenue: number;
  grossRevenue: number;
  revenueSource?: string;
};

export type YearlyInventoryTransferAvailabilityAssetEarningEvent = {
  eventId: string | null;
  eventLineId?: string | null;
  eventCode: string | null;
  r2ContractId: string | null;
  description: string | null;
  label: string;
  date?: string | null;
  endDate?: string | null;
  filledAt?: string | null;
  dispatchedAt?: string | null;
  returnedAt?: string | null;
  movementType?: string | null;
  status?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  qty?: number;
  chargeDays?: number | null;
  totalCharge?: number;
  grossRevenue?: number;
  netRevenue?: number;
  revenueSource?: string;
};

export type YearlyInventoryTransferAvailabilityAssetEarningSnapshot = {
  totalNetRevenue: number;
  totalGrossRevenue: number;
  revenueSource?: string;
  earningsByLocation: YearlyInventoryTransferAvailabilityAssetEarningLocation[];
  events?: YearlyInventoryTransferAvailabilityAssetEarningEvent[];
};

export type YearlyInventoryTransferAvailabilityAssetEarning = {
  assetId: string;
  barcode?: string | null;
  serialNumber?: string | null;
  status?: string | null;
  kitComponentId?: string | null;
  kitComponentItemId?: string | null;
  kitComponentCode?: string | null;
  kitComponentName?: string | null;
  kitComponentRequiredQty?: number | null;
  purchaseCost: number;
  totalNetRevenue: number;
  totalGrossRevenue: number;
  revenueSource?: string;
  earningsByLocation: YearlyInventoryTransferAvailabilityAssetEarningLocation[];
  events?: YearlyInventoryTransferAvailabilityAssetEarningEvent[];
  periodEarnings?: YearlyInventoryTransferAvailabilityAssetEarningSnapshot;
  allHistoryEarnings?: YearlyInventoryTransferAvailabilityAssetEarningSnapshot;
};

export type YearlyInventoryTransferAvailabilityKitComponent = {
  kitComponentId?: string | null;
  componentItemId: string;
  componentItemCode?: string | null;
  componentItemName?: string | null;
  requiredQty: number;
  ownedQty: number;
  sourceableQty: number;
  wholeOwnedQty: number;
  wholeSourceableQty: number;
  assetCount: number;
  sourceKinds?: string[];
};

export type YearlyInventoryTransferAvailabilityRevenueEvent = {
  eventId: string | null;
  eventCode: string | null;
  r2ContractId: string | null;
  description: string | null;
  label: string;
  date?: string | null;
  endDate?: string | null;
  qty?: number;
  chargeDays?: number | null;
  totalCharge?: number;
  grossRevenue?: number;
  netRevenue?: number;
  revenueSource?: 'item_event_link' | 'invoice_adjusted';
};

export type YearlyInventoryTransferAvailabilityCandidate = {
  locationId: string;
  locationName: string;
  siteid?: string | null;
  postalCode?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  isActive?: boolean;
  ownedQty: number;
  sourceableQty: number;
  assetCount: number;
  currentWindowPeakQty: number;
  historicalPeakQty: number;
  averageConcurrentQty: number;
  periodPeakQty: number;
  periodAverageMonthlyQty: number;
  periodRentalCount: number;
  periodRentalQty: number;
  sourceProtectionPeriodPeakQty?: number;
  sourceProtectionAverageMonthlyQty?: number;
  sourceProtectionPeriodRentalCount?: number;
  sourceProtectionPeriodRentalQty?: number;
  targetPeriodPeakQty?: number;
  targetPeriodRentalCount?: number;
  targetPeriodRentalQty?: number;
  sourceLocalPeriodPeakQty?: number;
  sourceLocalPeriodRentalCount?: number;
  otherProtectedPeriodPeakQty?: number;
  otherProtectedPeriodRentalCount?: number;
  targetTransferInCount?: number;
  targetTransferInQty?: number;
  targetTransferInPeakQty?: number;
  targetTransferInAverageQty?: number;
  protectedQty: number;
  transferableQty: number;
  maxAllocatableQty?: number;
  allocatableTransferableQty?: number;
  isRequestingLocation?: boolean;
  utilizationRatio: number;
  netRevenue: number;
  grossRevenue: number;
  purchaseCostBasis?: number | null;
  roiPerOwnedUnit: number | null;
  revenueToCostRatio: number | null;
  unitPurchaseCost: number | null;
  ownedExposureDays?: number | null;
  ownedExposureRatio?: number | null;
  annualizedNetRevenue?: number | null;
  projectedTwoYearRoi?: number | null;
  lastRentalDate?: string | null;
  lastRentalEvent?: {
    eventId: string | null;
    eventCode: string | null;
    r2ContractId: string | null;
    description: string | null;
    label: string;
  } | null;
  rentalEvents?: Array<{
    eventId: string | null;
    eventCode: string | null;
    r2ContractId: string | null;
    description: string | null;
    label: string;
    rentedAt?: string | null;
    date?: string | null;
    endDate?: string | null;
    usageLocationId?: string | null;
    qty?: number;
    chargeDays?: number | null;
    totalCharge?: number;
    grossRevenue?: number;
    netRevenue?: number;
    discountAmount?: number | null;
    revenueSource?: string | null;
  }>;
  rentalEventTotalCount?: number;
  closenessScore: number;
  closenessReason: string;
  closenessTargetLocationId?: string | null;
  closenessTargetLocationName?: string | null;
  closenessTargetPostalCode?: string | null;
  score: number;
  sourceKinds: string[];
  suggestionRank: number;
  isKitRollup?: boolean;
  kitComponents?: YearlyInventoryTransferAvailabilityKitComponent[];
  assets?: YearlyInventoryTransferAvailabilityAssetEarning[];
};

export type YearlyInventoryTransferAvailability = {
  groupId: string;
  itemId: string | null;
  requestedQty: number;
  status: 'red' | 'yellow' | 'green';
  statusLabel: string;
  totalTransferableQty: number;
  suggestedTransferQty: number;
  purchaseQty: number;
  targets: Array<{
    locationId: string;
    locationName: string;
    postalCode?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    requestedQty: number;
    reasonTypes?: string[];
    hasAdditionReason?: boolean;
    ownedQty?: number;
    transferInCount?: number;
    transferInQty?: number;
    transferInAverageQty?: number;
    transferInPeakQty?: number;
    netRevenue?: number;
    grossRevenue?: number;
    purchaseCostBasis?: number;
    roiPerOwnedUnit?: number | null;
    revenueToCostRatio?: number | null;
    unitPurchaseCost?: number | null;
    ownedExposureDays?: number | null;
    ownedExposureRatio?: number | null;
    annualizedNetRevenue?: number | null;
    projectedTwoYearRoi?: number | null;
    revenueLineCount?: number;
    purchaseCostSource?: string | null;
    revenueEvents?: YearlyInventoryTransferAvailabilityRevenueEvent[];
    revenueEventTotalCount?: number;
  }>;
  candidates: YearlyInventoryTransferAvailabilityCandidate[];
  suggestedAllocations: Array<{
    sourceLocationId: string;
    sourceLocationName: string;
    qty: number;
  }>;
  params: Required<YearlyInventoryTransferAvailabilityParams>;
  closenessBasis?: string;
  unavailableReason?: string;
};

export type YearlyInventoryTransferAvailabilityResponse = {
  groups: Record<string, YearlyInventoryTransferAvailability>;
  params: Required<YearlyInventoryTransferAvailabilityParams>;
  meta: Record<string, unknown>;
};

export async function fetchYearlyInventoryCampaigns(): Promise<YearlyInventoryCampaign[]> {
  const res = await apiFetch('/yearly-inventory/campaigns');
  const payload = await handleJson<{ items: YearlyInventoryCampaign[] }>(res);
  return payload.items || [];
}

export async function fetchYearlyInventoryReplacementSettings(): Promise<{
  reasons: YearlyInventoryReplacementReasonOption[];
}> {
  const res = await apiFetch('/yearly-inventory/replacement-settings');
  const payload = await handleJson<{ item: { reasons?: YearlyInventoryReplacementReasonOption[] } }>(res);
  return {
    reasons: Array.isArray(payload.item?.reasons) ? payload.item.reasons : [],
  };
}

export async function saveYearlyInventoryReplacementSettings(payload: {
  reasons: YearlyInventoryReplacementReasonOption[];
}): Promise<{
  reasons: YearlyInventoryReplacementReasonOption[];
}> {
  const res = await apiFetch('/yearly-inventory/replacement-settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: { reasons?: YearlyInventoryReplacementReasonOption[] } }>(res);
  return {
    reasons: Array.isArray(data.item?.reasons) ? data.item.reasons : [],
  };
}

export async function fetchYearlyItemActivityMetrics(payload: {
  locationId: string;
  itemIds: string[];
  year: number;
}): Promise<Record<string, YearlyItemActivityMetric>> {
  // The route deliberately rejects requests over 1,000 items. Catalog search can
  // expose several thousand rows, so keep each request comfortably below that
  // limit and merge the location/year facts for the caller.
  const itemIds = Array.from(new Set(
    (payload.itemIds || []).map((itemId) => String(itemId || '').trim()).filter(Boolean),
  ));
  if (!itemIds.length) return {};

  const batches: string[][] = [];
  for (let index = 0; index < itemIds.length; index += 500) {
    batches.push(itemIds.slice(index, index + 500));
  }
  const responses = await Promise.all(batches.map(async (batch) => {
    const res = await apiFetch('/yearly-inventory/item-activity', {
      method: 'POST',
      body: JSON.stringify({ ...payload, itemIds: batch }),
    });
    return handleJson<{ items: Record<string, YearlyItemActivityMetric> }>(res);
  }));
  return Object.assign({}, ...responses.map((data) => data.items || {}));
}

export async function fetchYearlyItemLocationActivity(payload: {
  itemId: string;
  year: number;
  locationIds?: string[];
}): Promise<YearlyItemLocationActivity> {
  const res = await apiFetch('/yearly-inventory/item-location-activity', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ items: Record<string, YearlyItemLocationActivity> }>(res);
  return data.items?.[payload.itemId] || {
    totals: {
      qtyTransferredIn: 0,
      timesTransferredIn: 0,
      timesRented: 0,
      qtyRented: 0,
    },
    locations: [],
  };
}

export async function fetchYearlyItemLocationFinancials(payload: {
  itemId: string;
  year: number;
  locationIds?: string[];
}): Promise<YearlyItemLocationFinancials> {
  const res = await apiFetch('/yearly-inventory/item-location-financials', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ items: Record<string, YearlyItemLocationFinancials> }>(res);
  return data.items?.[payload.itemId] || {
    totals: {
      netRevenue: 0,
      grossRevenue: 0,
      purchaseCostBasis: 0,
      unitPurchaseCost: null,
      inventoryQty: 0,
      assetCount: 0,
      revenueToCostRatio: null,
      roiPerOwnedUnit: null,
      historicalPeakQty: 0,
      averageConcurrentQty: 0,
      peakUtilizationRatio: null,
      averageUtilizationRatio: null,
      revenueLineCount: 0,
      invoiceAdjustedLineCount: 0,
      kitComponentLineCount: 0,
      purchaseCostSource: null,
    },
    locations: [],
  };
}

export async function fetchYearlyAssetLocationFinancials(payload: {
  assetId: string;
  year: number;
  locationIds?: string[];
}): Promise<YearlyAssetLocationFinancials> {
  const res = await apiFetch('/yearly-inventory/asset-location-financials', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ items: Record<string, YearlyAssetLocationFinancials> }>(res);
  return data.items?.[payload.assetId] || {
    assetId: payload.assetId,
    purchaseCost: 0,
    totals: {
      netRevenue: 0,
      grossRevenue: 0,
      usageCount: 0,
      purchaseCost: 0,
      revenueToCostRatio: null,
    },
    locations: [],
  };
}

export async function kickoffYearlyInventoryCampaign(payload: {
  campaignYear: number;
  title?: string;
  kickoffMessage?: string;
  startsAt?: string | null;
  endsAt?: string | null;
  locationIds?: string[];
}): Promise<{ campaignId: string; broadcastId: string }> {
  const res = await apiFetch('/yearly-inventory/kickoff', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ campaignId: string; broadcastId: string }>(res);
}

export async function updateYearlyInventoryCampaign(
  campaignId: string,
  payload: UpdateYearlyInventoryCampaignPayload,
): Promise<YearlyInventoryCampaign> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryCampaign }>(res);
  return data.item;
}

export async function closeYearlyInventoryCampaign(campaignId: string): Promise<YearlyInventoryCampaign> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/close`, { method: 'POST' });
  const data = await handleJson<{ item: YearlyInventoryCampaign }>(res);
  return data.item;
}

export async function deleteYearlyInventoryCampaign(campaignId: string): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to delete campaign');
  }
}

export async function fetchYearlyInventoryRequests(campaignId: string): Promise<YearlyInventoryRequest[]> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/requests`);
  const payload = await handleJson<{ items: YearlyInventoryRequest[] }>(res);
  return payload.items || [];
}

export async function ensureYearlyInventoryRequest(campaignId: string, locationId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/requests/${locationId}/ensure`, { method: 'POST' });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function fetchYearlyInventoryRequest(requestId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}`);
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function fetchAssignedActiveYearlyInventoryRequests(): Promise<AssignedYearlyInventoryRequest[]> {
  const res = await apiFetch('/yearly-inventory/requests/assigned-active');
  const payload = await handleJson<{ items: AssignedYearlyInventoryRequest[] }>(res);
  return payload.items || [];
}

export async function fetchMyYearlyInventoryPlannerBoard(): Promise<YearlyInventoryPlannerBoard> {
  const res = await apiFetch('/yearly-inventory/planner/me');
  const payload = await handleJson<{ item: YearlyInventoryPlannerBoard }>(res);
  return payload.item;
}

export async function updateMyYearlyInventoryPlannerNotes(
  payload: { notes: string | null },
): Promise<Pick<YearlyInventoryPlannerBoard, 'board_id' | 'owner_profile_id' | 'notes' | 'updated_at'>> {
  const res = await apiFetch('/yearly-inventory/planner/me', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{
    item: Pick<YearlyInventoryPlannerBoard, 'board_id' | 'owner_profile_id' | 'notes' | 'updated_at'>;
  }>(res);
  return data.item;
}

export async function updateMyYearlyInventoryPlannerPanels(
  payload: { panels: Array<{ key: string; name: string }> },
): Promise<{ board_id: string; panels: Array<{ key: string; name: string }> }> {
  const res = await apiFetch('/yearly-inventory/planner/me/panels', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: { board_id: string; panels: Array<{ key: string; name: string }> } }>(res);
  return data.item;
}

export async function addMyYearlyInventoryPlannerLine(
  payload: {
    itemId?: string | null;
    productId?: string | null;
    customItemName?: string | null;
    customMake?: string | null;
    customModel?: string | null;
    externalUrl?: string | null;
    requestedUnitPrice?: number | null;
    qtyNeeded: number;
    reasonType: YearlyInventoryReasonType;
    reasonDetail?: string | null;
    replacementReasonKey?: string | null;
    replacedItemId?: string | null;
    isExternalItem?: boolean;
    panelKey?: string | null;
    lineOrder?: number;
    priorityRank?: number;
  },
): Promise<YearlyInventoryPlannerLine> {
  const res = await apiFetch('/yearly-inventory/planner/me/lines', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryPlannerLine }>(res);
  return data.item;
}

export async function updateMyYearlyInventoryPlannerLine(
  lineId: string,
  payload: Partial<{
    itemId: string | null;
    productId: string | null;
    customItemName: string | null;
    customMake: string | null;
    customModel: string | null;
    externalUrl: string | null;
    requestedUnitPrice: number | null;
    qtyNeeded: number;
    reasonType: YearlyInventoryReasonType;
    reasonDetail: string | null;
    replacementReasonKey: string | null;
    replacedItemId: string | null;
    isExternalItem: boolean;
    panelKey: string | null;
    priorityRank: number;
    lineOrder: number;
  }>,
): Promise<YearlyInventoryPlannerLine> {
  const res = await apiFetch(`/yearly-inventory/planner/lines/${lineId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryPlannerLine }>(res);
  return data.item;
}

export async function deleteMyYearlyInventoryPlannerLine(lineId: string): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/planner/lines/${lineId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete planner line');
}

export async function fetchYearlyInventoryLineAttachments(
  target: YearlyInventoryAttachmentTarget,
): Promise<YearlyInventoryLineAttachment[]> {
  const path = target.kind === 'planner'
    ? `/yearly-inventory/planner/lines/${target.lineId}/attachments`
    : `/yearly-inventory/lines/${target.lineId}/attachments`;
  const res = await apiFetch(path);
  const data = await handleJson<{ items: YearlyInventoryLineAttachment[] }>(res);
  return Array.isArray(data.items) ? data.items : [];
}

export async function createMyYearlyInventoryPlannerAttachmentUploadSessions(
  lineId: string,
  files: File[],
): Promise<YearlyInventoryAttachmentUploadSession[]> {
  const res = await apiFetch(`/yearly-inventory/planner/lines/${lineId}/attachments/upload-sessions`, {
    method: 'POST',
    body: JSON.stringify({
      files: files.map((file) => ({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      })).map((file, clientIndex) => ({ ...file, clientIndex })),
    }),
  });
  const data = await handleJson<{ items: YearlyInventoryAttachmentUploadSession[] }>(res);
  return Array.isArray(data.items) ? data.items : [];
}

export async function completeMyYearlyInventoryPlannerAttachmentUpload(
  lineId: string,
  attachmentLinkId: string,
): Promise<YearlyInventoryLineAttachment> {
  const res = await apiFetch(
    `/yearly-inventory/planner/lines/${lineId}/attachments/${attachmentLinkId}/complete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  const data = await handleJson<{ item: YearlyInventoryLineAttachment }>(res);
  return data.item;
}

export async function deleteYearlyInventoryLineAttachment(
  target: YearlyInventoryAttachmentTarget,
  attachmentLinkId: string,
): Promise<void> {
  const path = target.kind === 'planner'
    ? `/yearly-inventory/planner/lines/${target.lineId}/attachments/${attachmentLinkId}`
    : `/yearly-inventory/lines/${target.lineId}/attachments/${attachmentLinkId}`;
  const res = await apiFetch(path, { method: 'DELETE' });
  if (!res.ok) await throwApiError(res);
}

export async function linkExistingMyYearlyInventoryPlannerAttachments(
  lineId: string,
  attachmentFileIds: string[],
  source?: YearlyInventoryAttachmentLinkSource | null,
): Promise<YearlyInventoryLineAttachment[]> {
  const res = await apiFetch(`/yearly-inventory/planner/lines/${lineId}/attachments/link-existing`, {
    method: 'POST',
    body: JSON.stringify({
      attachmentFileIds,
      sourceKind: source?.kind,
      sourceLineId: source?.lineId,
    }),
  });
  const data = await handleJson<{ items: YearlyInventoryLineAttachment[] }>(res);
  return Array.isArray(data.items) ? data.items : [];
}

export async function linkExistingYearlyInventoryRequestAttachments(
  lineId: string,
  attachmentFileIds: string[],
  source: YearlyInventoryAttachmentLinkSource,
): Promise<YearlyInventoryLineAttachment[]> {
  const res = await apiFetch(`/yearly-inventory/lines/${lineId}/attachments/link-existing`, {
    method: 'POST',
    body: JSON.stringify({
      attachmentFileIds,
      sourceKind: source.kind,
      sourceLineId: source.lineId,
    }),
  });
  const data = await handleJson<{ items: YearlyInventoryLineAttachment[] }>(res);
  return Array.isArray(data.items) ? data.items : [];
}

export class YearlyInventoryAttachmentUploadError extends Error {
  uploadedItems: YearlyInventoryLineAttachment[];
  failedIndexes: number[];

  constructor(message: string, uploadedItems: YearlyInventoryLineAttachment[], failedIndexes: number[]) {
    super(message);
    this.name = 'YearlyInventoryAttachmentUploadError';
    this.uploadedItems = uploadedItems;
    this.failedIndexes = failedIndexes;
  }
}

export async function uploadMyYearlyInventoryPlannerAttachments(
  lineId: string,
  files: File[],
  onStatus?: (index: number, status: YearlyInventoryAttachmentUploadStatus, error?: string) => void,
): Promise<YearlyInventoryLineAttachment[]> {
  if (!files.length) return [];
  const sessions = await createMyYearlyInventoryPlannerAttachmentUploadSessions(lineId, files);
  if (sessions.length !== files.length) {
    throw new Error('The screenshot upload session did not include every selected file.');
  }
  const sessionByClientIndex = new Map<number, YearlyInventoryAttachmentUploadSession>();
  for (const session of sessions) {
    const clientIndex = Number(session.client_index);
    if (
      !Number.isInteger(clientIndex)
      || clientIndex < 0
      || clientIndex >= files.length
      || sessionByClientIndex.has(clientIndex)
    ) {
      throw new Error('The screenshot upload session order was invalid. Choose the files again.');
    }
    sessionByClientIndex.set(clientIndex, session);
  }
  if (sessionByClientIndex.size !== files.length) {
    throw new Error('The screenshot upload session did not match every selected file.');
  }
  const results = await Promise.allSettled(files.map(async (file, index) => {
    const session = sessionByClientIndex.get(index);
    if (!session) throw new Error(`The upload session for ${file.name} is missing.`);
    let uploadedToStorage = false;
    try {
      onStatus?.(index, 'uploading');
      const { error: uploadError } = await supabase.storage
        .from(session.storage_bucket)
        .uploadToSignedUrl(session.storage_path, session.upload_token, file, {
          cacheControl: '3600',
          contentType: file.type,
          upsert: false,
      });
      if (uploadError) throw uploadError;
      uploadedToStorage = true;
      onStatus?.(index, 'completing');
      const item = await completeMyYearlyInventoryPlannerAttachmentUpload(
        lineId,
        session.attachment_link_id,
      );
      onStatus?.(index, 'uploaded');
      return item;
    } catch (error) {
      if (uploadedToStorage) {
        try {
          const reconciled = await completeMyYearlyInventoryPlannerAttachmentUpload(
            lineId,
            session.attachment_link_id,
          );
          if (reconciled?.attachment_link_id) {
            onStatus?.(index, 'uploaded');
            return reconciled;
          }
        } catch {
          try {
            const currentItems = await fetchYearlyInventoryLineAttachments({ kind: 'planner', lineId });
            const committed = currentItems.find(
              (item) => item.attachment_file_id === session.attachment_file_id,
            );
            if (committed) {
              onStatus?.(index, 'uploaded');
              return committed;
            }
          } catch {
            // Leave the tracked pending upload for server cleanup or a later completion retry.
          }
        }
      }
      const message = (error as Error)?.message || `Could not upload ${file.name}.`;
      onStatus?.(index, 'failed', message);
      if (!uploadedToStorage) {
        await deleteYearlyInventoryLineAttachment(
          { kind: 'planner', lineId },
          session.attachment_link_id,
        ).catch(() => undefined);
      }
      throw error;
    }
  }));
  const uploadedItems = results
    .filter((result): result is PromiseFulfilledResult<YearlyInventoryLineAttachment> => result.status === 'fulfilled')
    .map((result) => result.value);
  const failedIndexes = results
    .map((result, index) => (result.status === 'rejected' ? index : -1))
    .filter((index) => index >= 0);
  if (failedIndexes.length) {
    throw new YearlyInventoryAttachmentUploadError(
      `${failedIndexes.length} screenshot${failedIndexes.length === 1 ? '' : 's'} could not be uploaded.`,
      uploadedItems,
      failedIndexes,
    );
  }
  return uploadedItems;
}

export async function resequenceMyYearlyInventoryPlannerLines(
  payload: { entries: Array<{ lineId: string; lineOrder: number; panelKey?: string | null }> },
): Promise<void> {
  const res = await apiFetch('/yearly-inventory/planner/me/resequence-lines', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await handleJson<{ message?: string }>(res).catch(() => ({}));
    const message = body && typeof body === 'object' && 'message' in body ? (body as { message?: string }).message : undefined;
    throw new Error(message || 'Failed to reorder planner lines');
  }
}

export async function importMyYearlyInventoryPlannerLinesToRequest(payload: {
  requestId: string;
  mode: 'copy' | 'move';
  lineIds?: string[];
  panelKeys?: string[];
}): Promise<{ imported: number; mode: 'copy' | 'move'; removed: number }> {
  const res = await apiFetch('/yearly-inventory/planner/me/import-to-request', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ imported: number; mode: 'copy' | 'move'; removed: number }>(res);
}

export async function fetchYearlyInventoryMasterRequest(
  campaignId: string,
  options?: { includeDeleted?: boolean },
): Promise<YearlyInventoryMasterRequest> {
  const params = new URLSearchParams();
  if (options?.includeDeleted) params.set('includeDeleted', '1');
  const query = params.toString();
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master${query ? `?${query}` : ''}`);
  const payload = await handleJson<{ item: YearlyInventoryMasterRequest }>(res);
  return payload.item;
}

export async function fetchYearlyInventoryTransferAvailability(
  campaignId: string,
  payload: {
    groups: YearlyInventoryTransferAvailabilityGroupInput[];
    params?: YearlyInventoryTransferAvailabilityParams;
    includeAssetEarnings?: boolean;
    includeDetailEvents?: boolean;
    summaryOnly?: boolean;
    maxDetailEvents?: number;
    historyScope?: 'period' | 'allHistory';
  },
): Promise<YearlyInventoryTransferAvailabilityResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/transfer-availability`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<YearlyInventoryTransferAvailabilityResponse>(res);
}

export async function resequenceYearlyInventoryMasterGroups(
  campaignId: string,
  payload: { panelKey: string; orderedGroupIds: string[] },
): Promise<YearlyInventoryCampaign> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/resequence-groups`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryCampaign }>(res);
  return data.item;
}

export async function ensureYearlyInventoryRequestChatConversation(
  requestId: string,
): Promise<{ conversation_id: string }> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/chat-conversation`);
  const payload = await handleJson<{ item: { conversation_id: string } }>(res);
  return payload.item;
}

export type YearlyInventoryRequestLineInput = {
  itemId?: string | null;
  productId?: string | null;
  customItemName?: string | null;
  customMake?: string | null;
  customModel?: string | null;
  externalUrl?: string | null;
  requestedUnitPrice?: number | null;
  qtyNeeded: number;
  reasonType: YearlyInventoryReasonType;
  reasonDetail?: string | null;
  replacementReasonKey?: string | null;
  replacedItemId?: string | null;
  isExternalItem?: boolean;
  preferredProcurementType?: 'stock' | 'purchase' | 'cross_rental' | 'transfer' | null;
  preferredVendorId?: string | null;
  preferredSupplierId?: string | null;
  targetMarginPct?: number | null;
  expectedUnitCost?: number | null;
  convertToPermanentOnReceive?: boolean;
  panelKey?: string | null;
  lineOrder?: number;
  priorityRank?: number;
};

export async function addYearlyInventoryRequestLine(
  requestId: string,
  payload: YearlyInventoryRequestLineInput,
  options?: { masterMode?: boolean },
): Promise<YearlyInventoryRequestLine> {
  const suffix = options?.masterMode ? '?masterMode=1' : '';
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/lines${suffix}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryRequestLine }>(res);
  return data.item;
}

export async function addYearlyInventoryMasterRequestLines(
  campaignId: string,
  payload: { lines: Array<YearlyInventoryRequestLineInput & { requestId: string }> },
): Promise<{ items: YearlyInventoryRequestLine[]; created: number; requestCount: number }> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/request-lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ items: YearlyInventoryRequestLine[]; created: number; requestCount: number }>(res);
}

export async function updateYearlyInventoryRequestLine(
  lineId: string,
  payload: Partial<{
    itemId: string | null;
    productId: string | null;
    customItemName: string | null;
    customMake: string | null;
    customModel: string | null;
    externalUrl: string | null;
    requestedUnitPrice: number | null;
    qtyNeeded: number;
    reasonType: YearlyInventoryReasonType;
    reasonDetail: string | null;
    replacementReasonKey: string | null;
    replacedItemId: string | null;
    isExternalItem: boolean;
    preferredProcurementType: 'stock' | 'purchase' | 'cross_rental' | 'transfer' | null;
    preferredVendorId: string | null;
    preferredSupplierId: string | null;
    targetMarginPct: number | null;
    expectedUnitCost: number | null;
    convertToPermanentOnReceive: boolean;
    internalTransferPlan: Record<string, unknown> | null;
    panelKey: string | null;
    priorityRank: number;
    lineOrder: number;
  }>,
  options?: { masterMode?: boolean },
): Promise<YearlyInventoryRequestLine> {
  const suffix = options?.masterMode ? '?masterMode=1' : '';
  const res = await apiFetch(`/yearly-inventory/lines/${lineId}${suffix}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryRequestLine }>(res);
  return data.item;
}

export async function deleteYearlyInventoryRequestLine(lineId: string): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/lines/${lineId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete line');
}

export async function resequenceYearlyInventoryRequestLines(
  requestId: string,
  payload: { entries: Array<{ lineId: string; lineOrder: number; panelKey?: string | null }> },
): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/resequence-lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await handleJson<{ message?: string }>(res).catch(() => ({}));
    const message = body && typeof body === 'object' && 'message' in body ? (body as { message?: string }).message : undefined;
    throw new Error(message || 'Failed to reorder yearly inventory lines');
  }
}

export async function importYearlyInventoryRequestLines(
  requestId: string,
  payload: {
    rows: Array<{
      kind: 'existing' | 'external';
      sku?: string;
      description?: string;
      link?: string;
      price?: number | null;
      qty: number;
      priorityRank?: number;
      reasonType?: YearlyInventoryReasonType | string;
      reasonDetail?: string | null;
      panelKey?: string | null;
      panelName?: string | null;
    }>;
    panelKey?: string;
  },
): Promise<{ imported: number; errors: Array<{ row: number; message: string }>; items: YearlyInventoryRequestLine[] }> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/import-lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ imported: number; errors: Array<{ row: number; message: string }>; items: YearlyInventoryRequestLine[] }>(res);
}

export async function submitYearlyInventoryRequest(requestId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/submit`, { method: 'POST' });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function reopenYearlyInventoryRequest(requestId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/reopen`, { method: 'POST' });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function requestYearlyInventoryRequestRecall(
  requestId: string,
): Promise<{ item: YearlyInventoryRequest; taskIds: string[]; notificationIds?: string[]; alreadyPending?: boolean }> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/recall-request`, { method: 'POST' });
  return handleJson<{ item: YearlyInventoryRequest; taskIds: string[]; notificationIds?: string[]; alreadyPending?: boolean }>(res);
}

export async function decideYearlyInventoryRequestRecall(
  requestId: string,
  decision: 'approved' | 'denied',
  taskId?: string | null,
): Promise<YearlyInventoryRequest> {
  const body = JSON.stringify({ decision, taskId: taskId ?? null });
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/recall-request/decision`, {
    method: 'POST',
    body,
  });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function updateYearlyInventoryRequestNotes(
  requestId: string,
  payload: { notes: string | null },
): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/notes`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return data.item;
}

export async function updateYearlyInventoryRequestPanels(
  requestId: string,
  payload: { panels: Array<{ key: string; name: string }> },
): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/panels`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return data.item;
}

export async function assignYearlyInventoryRequestOwner(
  requestId: string,
  payload: { assigneeProfileIds: string[]; dueAt?: string | null; note?: string | null },
): Promise<{ item: YearlyInventoryRequest; taskIds: string[] }> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/assign`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson<{ item: YearlyInventoryRequest; taskIds: string[] }>(res);
}

export async function removeYearlyInventoryRequestLocation(requestId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/remove-location`, {
    method: 'POST',
  });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function restoreYearlyInventoryRequestLocation(requestId: string): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/restore-location`, {
    method: 'POST',
  });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function updateYearlyInventoryFulfillmentTransfers(
  requestId: string,
  payload: { transferOrderIds: string[]; note?: string | null },
): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/fulfillment-transfers`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return data.item;
}

export async function reviewYearlyInventoryLine(
  lineId: string,
  decision: 'approved' | 'rejected',
  note?: string | null,
): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/lines/${lineId}/review`, {
    method: 'POST',
    body: JSON.stringify({ decision, note: note ?? null }),
  });
  if (!res.ok) await throwApiError(res);
}

export async function approveYearlyInventoryMasterLines(
  campaignId: string,
  payload: {
    lineIds: string[];
    procurementType: 'purchase' | 'transfer';
    transferAllocations?: Array<{
      requestLineId: string;
      sourceLocationId: string;
      sourceLocationName?: string | null;
      qty: number;
    }>;
  },
): Promise<{
  ok: boolean;
  procurementType: 'purchase' | 'transfer';
  updated: number;
  transferIds: string[];
  transferLinkCount: number;
  stagedTransferCount?: number;
}> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/approve-lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function fetchYearlyInventoryMasterPlannedTransfers(
  campaignId: string,
): Promise<YearlyInventoryMasterPlannedTransfersResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-transfers`);
  return handleJson(res);
}

export async function updateYearlyInventoryMasterPlannedTransferLines(
  campaignId: string,
  payload: {
    updates: Array<{
      requestLineId: string;
      fromLocationId: string;
      toLocationId: string;
      transferOrderReference?: string | null;
      transferredQty?: number;
      transferStatus?: YearlyInventoryMasterPlannedTransferStatus;
      nextFromLocationId?: string;
    }>;
  },
): Promise<YearlyInventoryMasterPlannedTransfersResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-transfers/lines`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export const updateYearlyInventoryMasterPlannedTransferReferences =
  updateYearlyInventoryMasterPlannedTransferLines;

export async function fetchYearlyInventoryPlannedTransferShares(
  campaignId: string,
): Promise<YearlyInventoryPlannedTransferSharesResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-transfers/shares`);
  return handleJson(res);
}

export async function shareYearlyInventoryPlannedTransfers(
  campaignId: string,
  payload: { profileIds: string[] },
): Promise<{ ok: boolean; created: number; alreadyShared: number }> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-transfers/shares`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function revokeYearlyInventoryPlannedTransferShare(
  campaignId: string,
  profileId: string,
): Promise<{ ok: boolean; revoked: number }> {
  const res = await apiFetch(
    `/yearly-inventory/campaigns/${campaignId}/master/planned-transfers/shares/${encodeURIComponent(profileId)}`,
    { method: 'DELETE' },
  );
  return handleJson(res);
}

export async function fetchYearlyInventoryMasterPlannedPurchases(
  campaignId: string,
): Promise<YearlyInventoryMasterPlannedPurchasesResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases`);
  return handleJson(res);
}

export async function fetchYearlyInventoryPlannedPurchaseShares(
  campaignId: string,
): Promise<YearlyInventoryPlannedPurchaseSharesResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/shares`);
  return handleJson(res);
}

export async function shareYearlyInventoryPlannedPurchases(
  campaignId: string,
  payload: { profileIds: string[] },
): Promise<{ ok: boolean; created: number; alreadyShared: number }> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/shares`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function revokeYearlyInventoryPlannedPurchaseShare(
  campaignId: string,
  profileId: string,
): Promise<{ ok: boolean; revoked: number }> {
  const res = await apiFetch(
    `/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/shares/${encodeURIComponent(profileId)}`,
    { method: 'DELETE' },
  );
  return handleJson(res);
}

export async function fetchYearlyInventoryMasterPurchaseVendors(
  campaignId: string,
): Promise<YearlyInventoryPurchaseVendorOption[]> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/vendors`);
  const data = await handleJson<{ items: YearlyInventoryPurchaseVendorOption[] }>(res);
  return data.items || [];
}

export async function createYearlyInventoryMasterPurchaseVendor(
  campaignId: string,
  payload: { name: string },
): Promise<YearlyInventoryPurchaseVendorOption> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/vendors`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: YearlyInventoryPurchaseVendorOption }>(res);
  return data.item;
}

export async function resequenceYearlyInventoryMasterPlannedPurchaseLines(
  campaignId: string,
  payload: { orderedLineIds: string[] },
): Promise<{ ok: boolean; orderedLineIds: string[] }> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/resequence-lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function createYearlyInventoryMasterPlannedPurchaseTransfers(
  campaignId: string,
  payload: { batchId?: string; batchIds?: string[]; createAll?: boolean },
): Promise<YearlyInventoryMasterPlannedPurchasesResponse> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/create-transfers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function updateYearlyInventoryMasterPlannedPurchaseLines(
  campaignId: string,
  payload: {
    lineIds: string[];
    vendorId?: string | null;
    poNumber?: string | null;
    purchased?: boolean;
    purchaseStatus?: 'planned' | 'partial' | 'purchased' | string;
    purchasedQty?: number;
    purchaseAllocations?: Array<{
      locationId: string;
      locationName?: string | null;
      qty: number;
    }>;
    qtyNeeded?: number;
    requestedUnitPrice?: number | null;
    externalUrl?: string | null;
    purchaseNote?: string | null;
    priorityRank?: number;
    itemMake?: string | null;
    itemModel?: string | null;
  },
): Promise<YearlyInventoryMasterPlannedPurchasesResponse & { updated?: number }> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/planned-purchases/lines`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return handleJson(res);
}

export async function publishYearlyInventoryMasterApprovals(
  campaignId: string,
): Promise<YearlyInventoryCampaign> {
  const res = await apiFetch(`/yearly-inventory/campaigns/${campaignId}/master/publish-approvals`, {
    method: 'POST',
  });
  const data = await handleJson<{ item: YearlyInventoryCampaign }>(res);
  return data.item;
}

export async function cancelYearlyInventoryMasterTransferOrder(
  campaignId: string,
  transferId: string,
): Promise<{
  ok: boolean;
  transferId: string;
  affectedLineIds: string[];
  cancelledLinkCount: number;
  cancelledTransferLineCount: number;
  remainingApprovedTransferQtyByLineId: Record<string, number>;
}> {
  const res = await apiFetch(
    `/yearly-inventory/campaigns/${campaignId}/master/transfers/${transferId}/cancel`,
    { method: 'POST' },
  );
  return handleJson(res);
}

export async function reviewAllYearlyInventoryRequestLines(
  requestId: string,
  decision: 'approved' | 'rejected',
  note?: string | null,
): Promise<void> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/review-all`, {
    method: 'POST',
    body: JSON.stringify({ decision, note: note ?? null }),
  });
  if (!res.ok) await throwApiError(res);
}

export async function setYearlyInventoryRequestManagerDecision(
  requestId: string,
  decision: 'approved' | 'rejected',
): Promise<YearlyInventoryRequest> {
  const res = await apiFetch(`/yearly-inventory/requests/${requestId}/manager-decision`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
  const payload = await handleJson<{ item: YearlyInventoryRequest }>(res);
  return payload.item;
}

export async function fetchYearlyInventoryRollup(rollupId: string): Promise<any> {
  const res = await apiFetch(`/yearly-inventory/rollups/${rollupId}`);
  const payload = await handleJson<{ item: any }>(res);
  return payload.item;
}

export async function submitYearlyInventoryRollup(rollupId: string): Promise<any> {
  const res = await apiFetch(`/yearly-inventory/rollups/${rollupId}/submit`, { method: 'POST' });
  const payload = await handleJson<{ item: any }>(res);
  return payload.item;
}

export async function fetchYearlyInventoryRecommendations(rollupId: string, lineId: string): Promise<{
  lineSummary: Record<string, unknown>;
  recommendations: YearlyInventoryRecommendation[];
  allocationPreview: Record<string, unknown>;
  meta: Record<string, unknown>;
}> {
  const res = await apiFetch(`/yearly-inventory/rollups/${rollupId}/lines/${lineId}/recommendations`);
  return handleJson(res);
}

export async function saveYearlyInventoryRegionalLineDecision(
  regionalLineId: string,
  payload: {
    acceptedSourceLocationId?: string | null;
    acceptedTransferQty?: number | null;
    acceptedPermanentTransfer?: boolean | null;
    overrideReason?: string | null;
  },
): Promise<any> {
  const res = await apiFetch(`/yearly-inventory/regional-lines/${regionalLineId}/decision`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await handleJson<{ item: any }>(res);
  return data.item;
}
